import type { AssistantTool, AssistantToolResult } from '@rammetec/app-kit/assistant'
import { entityRegistry, repositoryResolver, type FilterAst } from '@rammetec/data-access'
import { rtLogService } from '@rammetec/core'
import {
  buildEntitySchema,
  buildFilterableColumns,
  emptyFilterModel,
  filterModelToAst,
  flattenConditions,
  type FilterModel,
  type SelectableColumn,
} from '@rammetec/app-kit/query'
import { formatDateBR } from '@/shared/forms/dateValue'
import { runDescribeFields } from '@/shared/crud/assistantDescribeFields'
import {
  APPLY_FILTER_PARAMETERS_SCHEMA,
  buildApplyFilterOperatorsHint,
  buildFilterModelFromFlat,
  formatUnknownFieldError,
  parseFlatFilterInput,
  resolveFilterableColumn,
} from '@/shared/crud/assistantFilterInput'
import type { AssistantQueryableEntity } from './assistantEntityCatalog'
import {
  buildDescribeFieldMeta,
  createAssistantCreateTools,
  type AssistantCreateToolsDeps,
  type RelatedOptionsCache,
} from './assistantCreateRecord'

const FIND_RECORDS_DEFAULT_LIMIT = 5
const FIND_RECORDS_MAX_LIMIT = 20
const DEFAULT_FIELD_COUNT = 3

/**
 * `campos: ['*']` guard (R6): full-record dump only for a handful of rows.
 * Above this, refuse — otherwise the model dumps a table and burns the next turn.
 */
export const FIND_RECORDS_STAR_MAX = 3

/**
 * Cap on distinct FK ids resolved to labels in one findRecords call.
 * Why 60: FIND_RECORDS_MAX_LIMIT (20) × 3 relations — the copy-one-record case fits
 * in one batch per target. Beyond that we leave remaining ids raw and note it
 * (never N×M getById, never stall the turn).
 */
export const RELATION_LABEL_ID_CAP = 60

/** Prefer these own-field paths (in order) after an identifier when picking defaults. */
const DESCRIPTIVE_PATHS = ['description', 'name', 'reference'] as const

const numberFmt = new Intl.NumberFormat('pt-BR')

/** SelectableColumn plus descriptor flags used when formatting / choosing defaults. */
export type DisplayColumn = SelectableColumn & {
  /** autoNumber or field name `version` — print as raw integer (no thousand separator). */
  rawInteger?: boolean
  /** autoNumber or unique — preferred default display column. */
  isIdentifier?: boolean
}

/** fieldPath → (recordId → display label). */
export type RelationLabelMaps = ReadonlyMap<string, ReadonlyMap<string, string>>

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isEmptyCell(value: unknown): boolean {
  return value === null || value === undefined || value === ''
}

/**
 * Attach identifier / raw-integer flags from the entity FieldDescriptor once
 * (SelectableColumn does not carry autoNumber / unique).
 */
export function enrichDisplayColumns(
  entityName: string,
  columns: readonly SelectableColumn[],
): DisplayColumn[] {
  const fields = entityRegistry.has(entityName)
    ? entityRegistry.get(entityName).fields
    : undefined
  const byName = new Map((fields ?? []).map((f) => [f.name, f]))

  return columns.map((col) => {
    const field = byName.get(col.path)
    const rawInteger = col.path === 'version' || field?.autoNumber === true
    const isIdentifier = field?.autoNumber === true || field?.unique === true
    if (!rawInteger && !isIdentifier) return col
    return {
      ...col,
      ...(rawInteger ? { rawInteger: true } : {}),
      ...(isIdentifier ? { isIdentifier: true } : {}),
    }
  })
}

/** Own fields only (depth 0) — schema built with maxDepth 0 so relations never expand. */
function ownFilterableColumns(entityName: string): {
  schema: ReturnType<typeof buildEntitySchema>
  columns: DisplayColumn[]
} {
  const schema = buildEntitySchema(entityName, { maxDepth: 0 })
  const base = buildFilterableColumns(schema).filter((c) => c.depth === 0)
  return { schema, columns: enrichDisplayColumns(entityName, base) }
}

function relationFieldError(entityLabel: string, field: string): string {
  return (
    `só campos próprios de ${entityLabel} (pedi "${field}"; sem caminho com ponto). ` +
    `Resolva a relação com data.findRelatedOptions (esta entidade + rótulo do FK + texto) ` +
    `e filtre pelo campo FK com eq no id. Liste FKs com data.describeFields.`
  )
}

function resolveOwnColumn(
  field: string,
  columns: readonly SelectableColumn[],
  entityLabel: string,
): { ok: true; column: SelectableColumn } | { ok: false; error: string } {
  if (field.includes('.')) {
    return { ok: false, error: relationFieldError(entityLabel, field) }
  }
  const col = resolveFilterableColumn(field, columns)
  if (!col) {
    return { ok: false, error: formatUnknownFieldError(field, columns) }
  }
  if (col.depth !== 0 || col.path.includes('.')) {
    return { ok: false, error: relationFieldError(entityLabel, field) }
  }
  return { ok: true, column: col }
}

/**
 * Default columns when `campos` is omitted: identifier (autoNumber/unique) + a
 * descriptive field when present, then declaration order up to DEFAULT_FIELD_COUNT.
 */
export function defaultDisplayColumns(columns: readonly DisplayColumn[]): DisplayColumn[] {
  const candidates = columns.filter((c) => !c.fk && c.path !== 'id')
  if (candidates.length === 0) return []

  const picked: DisplayColumn[] = []
  const used = new Set<string>()

  const identifier = candidates.find((c) => c.isIdentifier)
  if (identifier) {
    picked.push(identifier)
    used.add(identifier.path)
  }

  for (const path of DESCRIPTIVE_PATHS) {
    const col = candidates.find((c) => c.path === path && !used.has(c.path))
    if (col) {
      picked.push(col)
      used.add(col.path)
      break
    }
  }

  for (const c of candidates) {
    if (picked.length >= DEFAULT_FIELD_COUNT) break
    if (used.has(c.path)) continue
    picked.push(c)
    used.add(c.path)
  }

  return picked
}

/**
 * Drop columns that are empty on every returned row (keep at least one).
 * Exists to slim the **default** column set when `campos` was omitted — never apply
 * this to fields the model asked for. Empty cells render as "—" and that honesty
 * ends the retry loop (empty ≠ missing).
 * Also used for `campos: ['*']` (all own non-empty fields).
 */
export function omitUniversallyEmptyColumns(
  rows: readonly Record<string, unknown>[],
  columns: readonly DisplayColumn[],
): DisplayColumn[] {
  if (rows.length === 0 || columns.length <= 1) return [...columns]
  const kept = columns.filter((col) => rows.some((row) => !isEmptyCell(row[col.path])))
  return kept.length > 0 ? kept : [columns[0]!]
}

function candidateLabelFromRow(
  row: Record<string, unknown>,
  displayField: string,
): string {
  const raw = row[displayField]
  if (typeof raw === 'string' && raw.trim()) return raw.trim()
  if (typeof raw === 'number' && Number.isFinite(raw)) return String(raw)
  const id = row.id
  if (typeof id === 'string' && id.trim()) return id.trim()
  return '—'
}

/**
 * Batch-resolve FK ids → display labels for columns that have `fk`.
 * One getPage per target entity (id IN …). Caps distinct ids at RELATION_LABEL_ID_CAP.
 * Call only for columns the model asked for (explicit `campos` / `*`) — defaults
 * already exclude FKs via `!c.fk`.
 */
export async function resolveRelationDisplayLabels(
  rows: readonly Record<string, unknown>[],
  columns: readonly DisplayColumn[],
): Promise<{
  labelsByField: Map<string, Map<string, string>>
  unresolvedCount: number
  capped: boolean
}> {
  const labelsByField = new Map<string, Map<string, string>>()
  const fkColumns = columns.filter((c) => c.fk?.target)
  if (fkColumns.length === 0 || rows.length === 0) {
    return { labelsByField, unresolvedCount: 0, capped: false }
  }

  // Group field paths by target (+ displayField) so one query covers several FKs to the same entity.
  type TargetBucket = {
    target: string
    displayField: string
    fieldPaths: string[]
    ids: Set<string>
  }
  const byTarget = new Map<string, TargetBucket>()

  for (const col of fkColumns) {
    const target = col.fk!.target
    const displayField = col.fk!.displayField ?? 'description'
    const key = `${target}\0${displayField}`
    let bucket = byTarget.get(key)
    if (!bucket) {
      bucket = { target, displayField, fieldPaths: [], ids: new Set() }
      byTarget.set(key, bucket)
    }
    bucket.fieldPaths.push(col.path)
    for (const row of rows) {
      const raw = row[col.path]
      if (typeof raw === 'string' && raw.trim()) bucket.ids.add(raw.trim())
      else if (typeof raw === 'number' && Number.isFinite(raw)) bucket.ids.add(String(raw))
    }
  }

  const allIds: { bucket: TargetBucket; id: string }[] = []
  for (const bucket of byTarget.values()) {
    for (const id of bucket.ids) allIds.push({ bucket, id })
  }

  const capped = allIds.length > RELATION_LABEL_ID_CAP
  const toResolve = capped ? allIds.slice(0, RELATION_LABEL_ID_CAP) : allIds
  const resolveSet = new Map<TargetBucket, string[]>()
  for (const { bucket, id } of toResolve) {
    const list = resolveSet.get(bucket) ?? []
    list.push(id)
    resolveSet.set(bucket, list)
  }

  let unresolvedCount = capped ? allIds.length - RELATION_LABEL_ID_CAP : 0

  for (const [bucket, ids] of resolveSet) {
    const idToLabel = new Map<string, string>()
    try {
      const repo = await repositoryResolver.resolve<Record<string, unknown> & { id: string }>(
        bucket.target,
      )
      const filterAst: FilterAst = {
        kind: 'group',
        combinator: 'and',
        children: [{ kind: 'condition', field: 'id', op: 'in', values: ids }],
      }
      const page = await repo.getPage({
        limit: ids.length,
        offset: 0,
        filterAst,
      })
      for (const item of page.items) {
        const row = item as Record<string, unknown>
        const id = typeof row.id === 'string' ? row.id : String(row.id ?? '')
        if (!id) continue
        idToLabel.set(id, candidateLabelFromRow(row, bucket.displayField))
      }
    } catch (error) {
      rtLogService.warn('[assistant] relation label batch failed', {
        target: bucket.target,
        error,
      })
      unresolvedCount += ids.length
      continue
    }

    for (const id of ids) {
      if (!idToLabel.has(id)) unresolvedCount += 1
    }
    for (const fieldPath of bucket.fieldPaths) {
      const existing = labelsByField.get(fieldPath) ?? new Map<string, string>()
      for (const [id, label] of idToLabel) existing.set(id, label)
      labelsByField.set(fieldPath, existing)
    }
  }

  return { labelsByField, unresolvedCount, capped }
}

export function formatCellValue(
  value: unknown,
  col: DisplayColumn,
  relationLabels?: RelationLabelMaps,
): string {
  if (isEmptyCell(value)) return '—'

  // FK asked in `campos`: show displayField label, not the raw id.
  if (col.fk && relationLabels) {
    const id =
      typeof value === 'string'
        ? value.trim()
        : typeof value === 'number' && Number.isFinite(value)
          ? String(value)
          : ''
    if (id) {
      const label = relationLabels.get(col.path)?.get(id)
      if (label) return label
    }
  }

  if (col.enumLabels && (typeof value === 'string' || typeof value === 'number')) {
    const key = String(value)
    const label = col.enumLabels[key]
    if (label) return label
  }

  if (col.type === 'boolean') {
    if (value === true) return 'Sim'
    if (value === false) return 'Não'
  }

  if (col.type === 'date') {
    if (value instanceof Date || typeof value === 'string') {
      return formatDateBR(value) || '—'
    }
  }

  // Identifiers (autoNumber / version): raw integer — never thousand separators.
  if (col.rawInteger) {
    if (typeof value === 'number' && Number.isFinite(value)) return String(value)
    if (typeof value === 'string' && value.trim() !== '') {
      const n = Number(value)
      if (Number.isFinite(n)) return String(n)
      return value.trim()
    }
  }

  // pt-BR thousand/decimal separators — model must not invent them.
  if (typeof value === 'number' && Number.isFinite(value)) {
    return numberFmt.format(value)
  }
  if (col.type === 'number' && typeof value === 'string' && value.trim() !== '') {
    const n = Number(value)
    if (Number.isFinite(n)) return numberFmt.format(n)
  }

  if (Array.isArray(value)) {
    return value.map((v) => String(v)).join(', ')
  }

  if (typeof value === 'object') {
    return '—'
  }

  return String(value)
}

function formatRecordLine(
  row: Record<string, unknown>,
  columns: readonly DisplayColumn[],
  relationLabels?: RelationLabelMaps,
): string {
  return columns
    .map((col) => formatCellValue(row[col.path], col, relationLabels))
    .join(' — ')
}

function countNoun(
  entry: Pick<AssistantQueryableEntity, 'singularLabel' | 'pluralLabel'>,
  count: number,
): string {
  return count === 1 ? entry.singularLabel : entry.pluralLabel
}

/**
 * True when the filter is a single `eq` on `code` or `reference` — the model likely
 * guessed one field for a bare number the user said. Empty-result copy can then
 * suggest retrying with `combinator: 'or'` on both (request 0318).
 */
export function isSingleCodeOrReferenceEq(filter: FilterModel | undefined): boolean {
  if (!filter) return false
  const conditions = flattenConditions(filter)
  if (conditions.length !== 1) return false
  const c = conditions[0]!
  return c.operator === 'eq' && (c.path === 'code' || c.path === 'reference')
}

const CODE_OR_REFERENCE_RETRY_HINT =
  'Se o usuário citou um número solto, tente de novo com combinator "or": ' +
  'code eq (número) e reference eq (mesmo valor como texto).'

/**
 * Honest count copy for the model (and user). Never invent a total from page size.
 * - `total` known → exact count (PostgresRest / sources that honor countTotal).
 * - `total` omitted → use `hasMore` only (Firestore without adapter count).
 * - `filtered` → say the count already reflects the filter (avoids a redundant unfiltered call).
 * - `suggestCodeOrReference` → on empty filtered result after a lone code/reference eq,
 *   hint to retry with both (0318).
 */
export function buildFindRecordsMessage(
  entry: Pick<AssistantQueryableEntity, 'singularLabel' | 'pluralLabel'>,
  lines: string[],
  opts: {
    total?: number
    hasMore: boolean
    filtered?: boolean
    relationNote?: string
    suggestCodeOrReference?: boolean
  },
): string {
  const returned = lines.length
  const { total, hasMore, filtered, relationNote, suggestCodeOrReference } = opts
  const plural = entry.pluralLabel
  const filterNote = filtered ? ' com o filtro aplicado' : ''

  let base: string
  let empty = false
  if (total !== undefined) {
    if (total === 0) {
      empty = true
      base = `Nenhum registro em ${plural}${filterNote}.`
    } else if (returned === 0) {
      base = `${total} ${countNoun(entry, total)}${filterNote}.`
    } else {
      const list = lines.join('; ')
      if (returned >= total) {
        base = `${total} ${countNoun(entry, total)}${filterNote}: ${list}.`
      } else {
        base = `${total} ${countNoun(entry, total)}${filterNote}. Mostrando ${returned}: ${list}.`
      }
    }
  } else if (returned === 0) {
    // Exact total unavailable for this source.
    if (hasMore) {
      base = `Há ${plural}, mas o total exato não está disponível para essa entidade.`
    } else {
      empty = true
      base = `Nenhum registro em ${plural}${filterNote}.`
    }
  } else {
    const list = lines.join('; ')
    if (!hasMore) {
      base = `${returned} ${countNoun(entry, returned)}${filterNote}: ${list}.`
    } else {
      base =
        `Mais de ${returned} ${countNoun(entry, returned)}${filterNote}. Mostrando ${returned}: ${list}. ` +
        `Total exato não disponível para essa entidade.`
    }
  }

  if (empty && suggestCodeOrReference) {
    base = `${base} ${CODE_OR_REFERENCE_RETRY_HINT}`
  }

  return relationNote ? `${base} ${relationNote}` : base
}

type FindRecordsParams = {
  entidade: string
  filter?: FilterModel
  /** Explicit columns; mutually exclusive with `starFields`. */
  campos?: DisplayColumn[]
  /** `campos: ['*']` — all own non-empty fields (guarded by FIND_RECORDS_STAR_MAX). */
  starFields?: boolean
  limite: number
  ordenar?: { field: string; direction: 'asc' | 'desc' }
}

/**
 * Global read/write tools for entity search + silent create (requests 0293 / 0295 / 0307).
 * `queryable` is the enum catalog — rebuild when its fingerprint changes.
 * `createDeps` enables `data.createRecord` for creatable entities.
 * `data.findRelatedOptions` is registered for any queryable entity that has FK fields
 * (feeds findRecords filters and createRecord).
 */
export function createAssistantDataTools(
  queryable: readonly AssistantQueryableEntity[],
  createDeps?: AssistantCreateToolsDeps,
): AssistantTool[] {
  const byLabel = new Map(queryable.map((e) => [e.label, e]))
  const enumLabels = queryable.map((e) => e.label)
  const relatedCache: RelatedOptionsCache = new Map()

  const commonFieldsHint =
    'Quase tudo tem auditoria (inclusionDate, lastModifiedDate, inclusionUserId, lastModifiedUserId) e version; a maioria dos cadastros tem active e inactivationDate; documentos (pedido, ordem de compra, ordem de produção, nota fiscal, recebimento, expedição) usam status em vez de active.'

  const describeFields: AssistantTool<{ entidade: string; busca?: string }> = {
    name: 'data.describeFields',
    description:
      `${commonFieldsHint} ` +
      'Lista campos próprios pesquisáveis da entidade (caminho, rótulo, tipo, obrigatório e enums). ' +
      'Em entidades incluíveis, marca também o que o serviço preenche (autoNumber, auditoria, ciclo de vida). ' +
      'Use busca para restringir; no máximo 30. Só campos próprios nesta versão.',
    parameters: {
      type: 'object',
      properties: {
        entidade: {
          type: 'string',
          ...(enumLabels.length > 0 ? { enum: enumLabels } : {}),
          description: 'Rótulo da entidade (enum).',
        },
        busca: { type: 'string', description: 'Trecho do caminho ou rótulo (opcional).' },
      },
      required: ['entidade'],
      additionalProperties: false,
    },
    kind: 'read',
    confirm: 'never',
    validate: (raw) => {
      if (!isRecord(raw)) return { ok: false, error: 'não entendi: parâmetros inválidos' }
      if (typeof raw.entidade !== 'string' || !raw.entidade.trim()) {
        return {
          ok: false,
          error: `não entendi: informe a entidade. Aceitas: ${enumLabels.join(', ') || '(nenhuma neste perfil)'}`,
        }
      }
      if (!byLabel.has(raw.entidade)) {
        return {
          ok: false,
          error: `entidade desconhecida: "${raw.entidade}". Aceitas: ${enumLabels.join(', ') || '(nenhuma neste perfil)'}`,
        }
      }
      if (raw.busca !== undefined && typeof raw.busca !== 'string') {
        return { ok: false, error: 'não entendi: busca deve ser texto' }
      }
      return {
        ok: true,
        params:
          raw.busca !== undefined
            ? { entidade: raw.entidade, busca: raw.busca }
            : { entidade: raw.entidade },
      }
    },
    confirmSummary: (p) =>
      p.busca?.trim()
        ? `Listar campos de ${p.entidade} que contenham "${p.busca.trim()}"`
        : `Listar campos pesquisáveis de ${p.entidade}`,
    run: (params) => {
      const entry = byLabel.get(params.entidade)
      if (!entry) {
        return { ok: false, message: `Entidade desconhecida: "${params.entidade}".` }
      }
      try {
        const { columns } = ownFilterableColumns(entry.entity)
        const fieldMeta = buildDescribeFieldMeta(entry.entity, entry.creatable === true)
        return runDescribeFields(columns, params.busca ?? '', {
          preferOwnWhenEmpty: false,
          fieldMeta,
        })
      } catch (error) {
        rtLogService.warn('[assistant] data.describeFields failed', {
          entity: entry.entity,
          error,
        })
        return {
          ok: false,
          message: `Não consegui listar campos de ${entry.label}. Tente de novo ou abra o módulo.`,
        }
      }
    },
  }

  const findRecords: AssistantTool<FindRecordsParams> = {
    name: 'data.findRecords',
    description:
      `${commonFieldsHint} ` +
      'Lista e conta registros (somente leitura); não soma, não calcula média e não agrega — se pedirem isso, diga que ainda não sabe fazer. ' +
      'Mesmo filtro plano do grid.applyFilter. ' +
      `Limite padrão ${FIND_RECORDS_DEFAULT_LIMIT}, máximo ${FIND_RECORDS_MAX_LIMIT}. ` +
      'Sem filtro = todos. Campos omitidos → identificador + campo descritivo (quando existirem). ' +
      `campos: ["*"] = registro inteiro (campos próprios não vazios); só com limite ≤ ${FIND_RECORDS_STAR_MAX} e no máximo ${FIND_RECORDS_STAR_MAX} registros. ` +
      'Campo de relação pedido em campos (ou em *) sai pelo rótulo (displayField), não pelo id. ' +
      'Número solto do usuário ("o 8008", "abre o 1203"): filtre code eq (número) OU reference eq (mesmo valor como texto) numa chamada só (combinator "or") — não escolha um dos dois. ' +
      'Dois resultados: mostre e pergunte qual; não desempatar sozinho. ' +
      'Para filtrar pelo nome de um relacionado (cliente, grupo, fábrica…): ' +
      '1) data.findRelatedOptions nesta entidade + rótulo do campo FK + o texto; ' +
      '2) data.findRecords com filtro no campo FK (sem ponto) e eq no id. ' +
      'Ao ordenar, registro sem valor no campo pode vir primeiro (desc): para maior/menor, filtre isNotEmpty nesse campo. ' +
      `${buildApplyFilterOperatorsHint()} ` +
      'Caminhos: data.describeFields. Só campos próprios no filtro (sem ponto). Total exato só quando a fonte contar; senão use a mensagem (hasMore).',
    parameters: {
      type: 'object',
      properties: {
        entidade: {
          type: 'string',
          ...(enumLabels.length > 0 ? { enum: enumLabels } : {}),
          description: 'Rótulo da entidade (enum).',
        },
        filtro: {
          ...APPLY_FILTER_PARAMETERS_SCHEMA,
          description:
            'Filtro no esquema plano (mesmo do grid.applyFilter). Omitir = todos. ' +
            'Só campos próprios (sem ponto). Relação por nome: data.findRelatedOptions → eq no FK.',
        },
        campos: {
          type: 'array',
          items: { type: 'string' },
          description:
            'Rótulos ou caminhos a trazer (próprios). ["*"] = todos os próprios não vazios (máx. ' +
            `${FIND_RECORDS_STAR_MAX} registros). Omitir = identificador + descritivo.`,
        },
        limite: {
          type: 'integer',
          minimum: 1,
          maximum: FIND_RECORDS_MAX_LIMIT,
          description: `Quantidade de linhas (padrão ${FIND_RECORDS_DEFAULT_LIMIT}, máx. ${FIND_RECORDS_MAX_LIMIT}).`,
        },
        ordenar: {
          type: 'object',
          properties: {
            campo: { type: 'string', description: 'Caminho ou rótulo do campo.' },
            direcao: { type: 'string', enum: ['asc', 'desc'] },
          },
          required: ['campo', 'direcao'],
          additionalProperties: false,
        },
      },
      required: ['entidade'],
      additionalProperties: false,
    },
    kind: 'read',
    confirm: 'never',
    validate: (raw) => {
      if (!isRecord(raw)) return { ok: false, error: 'não entendi: parâmetros inválidos' }
      if (typeof raw.entidade !== 'string' || !raw.entidade.trim()) {
        return {
          ok: false,
          error: `não entendi: informe a entidade. Aceitas: ${enumLabels.join(', ') || '(nenhuma neste perfil)'}`,
        }
      }
      const entry = byLabel.get(raw.entidade)
      if (!entry) {
        return {
          ok: false,
          error: `entidade desconhecida: "${raw.entidade}". Aceitas: ${enumLabels.join(', ') || '(nenhuma neste perfil)'}`,
        }
      }

      let columns: DisplayColumn[]
      try {
        columns = ownFilterableColumns(entry.entity).columns
      } catch {
        return {
          ok: false,
          error: `esquema indisponível para ${entry.label}; não posso buscar agora`,
        }
      }

      let filter: FilterModel | undefined
      if (raw.filtro !== undefined) {
        if (!isRecord(raw.filtro)) {
          return { ok: false, error: 'não entendi: "filtro" deve ser um objeto { combinator, conditions?, groups? }' }
        }
        const relationHit = findRelationFieldInFlat(raw.filtro)
        if (relationHit) {
          return { ok: false, error: relationFieldError(entry.label, relationHit) }
        }
        const parsed = parseFlatFilterInput(raw.filtro)
        if (!parsed.ok) return parsed
        const built = buildFilterModelFromFlat(parsed.input, columns)
        if (!built.ok) return built
        filter = built.model
      }

      let limite = FIND_RECORDS_DEFAULT_LIMIT
      if (raw.limite !== undefined) {
        if (typeof raw.limite !== 'number' || !Number.isInteger(raw.limite)) {
          return {
            ok: false,
            error: `não entendi: "limite" deve ser inteiro de 1 a ${FIND_RECORDS_MAX_LIMIT}`,
          }
        }
        if (raw.limite < 1 || raw.limite > FIND_RECORDS_MAX_LIMIT) {
          return {
            ok: false,
            error: `limite ${raw.limite} fora do intervalo; use de 1 a ${FIND_RECORDS_MAX_LIMIT}`,
          }
        }
        limite = raw.limite
      }

      let campos: DisplayColumn[] | undefined
      let starFields = false
      if (raw.campos !== undefined) {
        if (!Array.isArray(raw.campos) || raw.campos.some((c) => typeof c !== 'string')) {
          return { ok: false, error: 'não entendi: "campos" deve ser lista de textos (rótulos ou caminhos)' }
        }
        if (raw.campos.length === 0) {
          return { ok: false, error: 'não entendi: "campos" vazio — omita o parâmetro ou informe ao menos um' }
        }
        if (raw.campos.length === 1 && raw.campos[0] === '*') {
          starFields = true
          if (limite > FIND_RECORDS_STAR_MAX) {
            return {
              ok: false,
              error:
                `campos: ["*"] só com limite ≤ ${FIND_RECORDS_STAR_MAX} (pedi ${limite}). ` +
                `Baixe o limite ou peça campos nomeados.`,
            }
          }
        } else {
          if (raw.campos.includes('*')) {
            return {
              ok: false,
              error:
                'campos: ["*"] deve ser o único item da lista — não misture com outros campos. ' +
                'Ou use só "*" ou liste rótulos/caminhos.',
            }
          }
          const resolved: DisplayColumn[] = []
          for (const name of raw.campos) {
            const r = resolveOwnColumn(name, columns, entry.label)
            if (!r.ok) return r
            // Prefer the enriched column (rawInteger / isIdentifier) when present.
            const enriched = columns.find((c) => c.path === r.column.path) ?? r.column
            resolved.push(enriched)
          }
          campos = resolved
        }
      }

      let ordenar: FindRecordsParams['ordenar']
      if (raw.ordenar !== undefined) {
        if (!isRecord(raw.ordenar)) {
          return { ok: false, error: 'não entendi: "ordenar" deve ser { campo, direcao }' }
        }
        if (typeof raw.ordenar.campo !== 'string' || !raw.ordenar.campo.trim()) {
          return { ok: false, error: 'não entendi: "ordenar.campo" é obrigatório' }
        }
        if (raw.ordenar.direcao !== 'asc' && raw.ordenar.direcao !== 'desc') {
          return { ok: false, error: 'não entendi: "ordenar.direcao" deve ser "asc" ou "desc"' }
        }
        const r = resolveOwnColumn(raw.ordenar.campo, columns, entry.label)
        if (!r.ok) return r
        ordenar = { field: r.column.path, direction: raw.ordenar.direcao }
      }

      return {
        ok: true,
        params: {
          entidade: raw.entidade,
          ...(filter ? { filter } : {}),
          ...(campos ? { campos } : {}),
          ...(starFields ? { starFields: true } : {}),
          limite,
          ...(ordenar ? { ordenar } : {}),
        },
      }
    },
    confirmSummary: (p) => `Buscar em ${p.entidade}`,
    run: async (params): Promise<AssistantToolResult> => {
      const entry = byLabel.get(params.entidade)
      if (!entry) {
        return { ok: false, message: `Entidade desconhecida: "${params.entidade}".` }
      }

      let schema: ReturnType<typeof buildEntitySchema>
      let columns: DisplayColumn[]
      try {
        ;({ schema, columns } = ownFilterableColumns(entry.entity))
      } catch (error) {
        rtLogService.warn('[assistant] data.findRecords schema failed', {
          entity: entry.entity,
          error,
        })
        return {
          ok: false,
          message: `Esquema indisponível para ${entry.label}; não busquei.`,
        }
      }

      const starFields = params.starFields === true
      const explicitCampos = params.campos
      const chosenColumns = starFields
        ? columns.filter((c) => c.path !== 'id')
        : (explicitCampos ?? defaultDisplayColumns(columns))
      if (chosenColumns.length === 0) {
        return {
          ok: false,
          message: `Nenhum campo próprio para exibir em ${entry.label}.`,
        }
      }

      const filterModel = params.filter ?? emptyFilterModel()
      const { ast, ignoredCount } = filterModelToAst(filterModel, schema)
      if (ignoredCount > 0) {
        return {
          ok: false,
          message:
            ignoredCount === 1
              ? 'Não busquei: 1 condição usa caminho de relação (sem ponto no filtro). Resolva com data.findRelatedOptions e filtre o FK com eq no id.'
              : `Não busquei: ${ignoredCount} condições usam caminho de relação (sem ponto no filtro). Resolva com data.findRelatedOptions e filtre o FK com eq no id.`,
        }
      }

      try {
        const repo = await repositoryResolver.resolve<Record<string, unknown> & { id: string }>(
          entry.entity,
        )
        const page = await repo.getPage({
          limit: params.limite,
          offset: 0,
          filterAst: ast,
          sort: params.ordenar,
          countTotal: true,
        })

        const total = page.total
        const totalKnown = total !== undefined
        const rows = page.items as Record<string, unknown>[]

        if (starFields) {
          const matchCount = totalKnown ? total : rows.length
          const overByTotal = totalKnown && total > FIND_RECORDS_STAR_MAX
          const overByPage = !totalKnown && (rows.length > FIND_RECORDS_STAR_MAX || page.hasMore)
          if (overByTotal || overByPage || matchCount > FIND_RECORDS_STAR_MAX) {
            const countHint = totalKnown
              ? `${total} registro${total === 1 ? '' : 's'}`
              : page.hasMore
                ? `mais de ${rows.length}`
                : `${rows.length}`
            return {
              ok: false,
              message:
                `campos: ["*"] casou com ${countHint} — teto é ${FIND_RECORDS_STAR_MAX}. ` +
                `Aperte o filtro (ex.: código/id) ou peça campos nomeados em vez de "*".`,
            }
          }
        }

        // Explicit named `campos`: keep every asked column (empty → "—").
        // Defaults and `*`: drop columns empty on every returned row.
        const displayColumns =
          explicitCampos !== undefined
            ? chosenColumns
            : omitUniversallyEmptyColumns(rows, chosenColumns)

        // Resolve FK labels only when the model asked for those columns (named campos or *).
        // Defaults exclude FKs (`!c.fk`); no batch then.
        const shouldResolveRelations =
          (explicitCampos !== undefined || starFields) &&
          displayColumns.some((c) => c.fk?.target)
        let relationLabels: RelationLabelMaps | undefined
        let relationNote: string | undefined
        if (shouldResolveRelations) {
          const resolved = await resolveRelationDisplayLabels(rows, displayColumns)
          relationLabels = resolved.labelsByField
          if (resolved.capped || resolved.unresolvedCount > 0) {
            relationNote = resolved.capped
              ? `Alguns rótulos de relação não resolvidos (teto ${RELATION_LABEL_ID_CAP} ids); ids ficaram crus.`
              : `Alguns rótulos de relação não resolvidos (${resolved.unresolvedCount}); ids ficaram crus.`
          }
        }

        const lines = rows.map((row) => formatRecordLine(row, displayColumns, relationLabels))
        const returned = lines.length
        const truncated = totalKnown ? total > returned : page.hasMore
        const hasCodeAndReference =
          columns.some((c) => c.path === 'code') && columns.some((c) => c.path === 'reference')
        const suggestCodeOrReference =
          hasCodeAndReference && isSingleCodeOrReferenceEq(params.filter)

        return {
          ok: true,
          message: buildFindRecordsMessage(entry, lines, {
            ...(totalKnown ? { total } : {}),
            hasMore: page.hasMore,
            filtered: params.filter !== undefined,
            ...(relationNote ? { relationNote } : {}),
            ...(suggestCodeOrReference ? { suggestCodeOrReference: true } : {}),
          }),
          data: {
            ...(totalKnown ? { total } : {}),
            totalKnown,
            returned,
            truncated,
          },
        }
      } catch (error) {
        rtLogService.warn('[assistant] data.findRecords failed', {
          entity: entry.entity,
          error,
        })
        return {
          ok: false,
          message: `Não consegui buscar ${entry.pluralLabel}. Tente de novo ou abra o módulo.`,
        }
      }
    },
  }

  return [
    describeFields,
    findRecords,
    ...createAssistantCreateTools(queryable, relatedCache, createDeps),
  ]
}

/** Walk a raw flat filter for dotted field paths (relation) before building the model. */
function findRelationFieldInFlat(raw: Record<string, unknown>): string | null {
  const check = (field: unknown): string | null => {
    if (typeof field === 'string' && field.includes('.')) return field
    return null
  }
  if (Array.isArray(raw.conditions)) {
    for (const c of raw.conditions) {
      if (isRecord(c)) {
        const hit = check(c.field)
        if (hit) return hit
      }
    }
  }
  if (Array.isArray(raw.groups)) {
    for (const g of raw.groups) {
      if (!isRecord(g) || !Array.isArray(g.conditions)) continue
      for (const c of g.conditions) {
        if (isRecord(c)) {
          const hit = check(c.field)
          if (hit) return hit
        }
      }
    }
  }
  return null
}
